import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { setUserToken, useUserData } from '@24i/nxg-sdk-smartott-shared/src/context/UserData';
import { logError } from '@24i/nxg-core-utils/src/logger';
import { useStore } from '@24i/nxg-sdk-smartott/src/context/ApplicationStore';
import { ApplicationStates, useAppStart } from '@24i/nxg-sdk-smartott/src/context/AppStartContext';
import { WebRouter, useRouter } from '@24i/nxg-core-router/src/NextRouter';
import { useModal } from '@24i/nxg-sdk-gluons/src/context/Modal';
import { getTelegramWebApp } from '@24i/nxg-sdk-higgs/src/utils';

import { parseUrl } from '../../utils';
import AuthView from '../../components/AuthView/index.web';
import { useAppNavigation } from '../../../../../../context/AppNavigation';
import {
    getOpenIDRedirectPage,
    saveOpenIDRedirectPage,
    removeOpenIDRedirectPage,
} from '../../../../../../utils/navigation';
import { SOTT_DEFAULT_WEB_SCREENS } from '../../../../../../navigation/constants';
import { useAuthOpenID } from '../../../../../../hooks/useAuth/providers/openID';
import { usePageShow } from '../../../../../../hooks/usePageShow/index.web';
import withErrorModal, {
    WithErrorModalProps,
} from '../../../../../../components/GenericModal/hoc/ErrorModal';

// this flag need to prevent fetching. We can't working with useRef() because data not saved between mounted/unmounted
// after invalidate queries. We use this component only for openID auth process. We create only one component instance
// in future it can be changed
let isFetching = false;

const OpenIDProvider: React.FC<WithErrorModalProps> = ({ openErrorModal }) => {
    const userDataClient = useUserData();
    const { appState } = useAppStart();
    const codeFromUrl = useRef('');
    const { userData } = useStore();
    const { previousRoute } = useAppNavigation();
    const { login } = useAuthOpenID();
    const isRedirecting = useRef(false);
    const { closeModal } = useModal();
    const { t } = useTranslation();

    const router = useRouter() as WebRouter;
    const TelegramWebApp = getTelegramWebApp();

    // Consolidated Telegram WebApp helper function
    const handleTelegramFullscreen = useCallback(
        (action: 'exit' | 'enter') => {
            if (!TelegramWebApp) return;

            if (action === 'exit') {
                TelegramWebApp.exitFullscreen?.();
                TelegramWebApp.expand?.();
            } else if (action === 'enter') {
                // When entering fullscreen, only check for TelegramWebApp availability
                // This makes it work both for logged in users and on unmount
                TelegramWebApp.requestFullscreen?.();
            }
        },
        [TelegramWebApp]
    );

    const redirectToHomePage = () => {
        const homeLink = SOTT_DEFAULT_WEB_SCREENS.Home.getLink();
        router.push(homeLink.href, homeLink.as);
    };

    const redirectToOriginalPage = useCallback(async () => {
        try {
            const redirectPage = await getOpenIDRedirectPage();
            if (redirectPage) {
                isRedirecting.current = true;

                await router.push(redirectPage, redirectPage);
                await removeOpenIDRedirectPage();
                return true;
            }
            return false;
        } catch (e) {
            isRedirecting.current = false;
            return false;
        }
    }, [router]);

    usePageShow(redirectToOriginalPage);

    useEffect(() => {
        if (!previousRoute) {
            const navEntries = performance.getEntriesByType(
                'navigation'
            ) as PerformanceNavigationTiming[];
            const navType = navEntries[0]?.type as string;

            if (navType === 'back_forward') redirectToOriginalPage();
        }
    }, [previousRoute]);

    useEffect(() => {
        const routerQueryCode = router.query?.code;
        if (routerQueryCode && typeof routerQueryCode === 'string') {
            codeFromUrl.current = routerQueryCode;
        } else if (router.asPath && router.asPath.includes('?code=')) {
            // in case router query is not parsed on refresh
            codeFromUrl.current = parseUrl(router.asPath).code;
        }
    }, [router.query?.code, router.asPath]);

    useEffect(() => {
        const getUserData = async () => {
            if (isFetching) return;

            try {
                isFetching = true;
                const { token } = await userDataClient.getUserOauthToken(codeFromUrl.current);
                codeFromUrl.current = '';
                if (!token) return;

                await setUserToken(token);
                await login();
            } catch (error) {
                logError(error);
                openErrorModal(
                    t('error.B00.title'),
                    t('error.B00.body'),
                    false,
                    undefined,
                    undefined,
                    async () => {
                        const isRedirected = await redirectToOriginalPage();
                        if (!isRedirected) {
                            redirectToHomePage();
                        }
                    }
                );
            } finally {
                isFetching = false;
            }
        };

        if (codeFromUrl.current && appState !== ApplicationStates.notInitialized && !userData) {
            getUserData();
        }
    }, [codeFromUrl.current, appState, userData]);

    const saveRedirectionPage = async (route: string) => {
        // for now we ignore /register page because we provide redirect from /register to /signin
        // and we need to avoid loop between this two pages [REVRY-113]
        const ignoreList = [
            SOTT_DEFAULT_WEB_SCREENS.SignUp.getLink()?.href,
            SOTT_DEFAULT_WEB_SCREENS.SignIn.getLink()?.href,
            SOTT_DEFAULT_WEB_SCREENS.SignOut.getLink()?.href,
        ];
        if (ignoreList.some((v) => route.includes(v))) return;

        await saveOpenIDRedirectPage(route);
    };

    useEffect(() => {
        const handleRedirection = async () => {
            if (previousRoute) {
                await saveRedirectionPage(previousRoute);
            }
        };

        handleRedirection().catch();
    }, [previousRoute]);

    useEffect(() => {
        const fetchServiceURL = async () => {
            const { redirectUrl: url } = await userDataClient.getOAuthUrl();

            if (typeof window !== 'undefined' && !isRedirecting.current) {
                closeModal();
                handleTelegramFullscreen('exit');
                window.location.href = url;
            }
        };
        if (!codeFromUrl.current && !userData && router.isReady) fetchServiceURL();
    }, [router.isReady, userData, closeModal, handleTelegramFullscreen]);

    // Combined Telegram WebApp effect
    useEffect(() => {
        // Exit fullscreen in Telegram WebApp at the beginning
        handleTelegramFullscreen('exit');

        // Enter fullscreen when component unmounts
        return () => handleTelegramFullscreen('enter');
    }, [handleTelegramFullscreen]);

    // Enter fullscreen when user data becomes available
    useEffect(() => {
        if (userData) {
            handleTelegramFullscreen('enter');
        }
    }, [userData, handleTelegramFullscreen]);

    return <AuthView />;
};

export default withErrorModal(OpenIDProvider);
